import {gql} from '@apollo/client'
import {get} from 'object-path-immutable'
import {useMemo} from 'react'
import {
  ActionFlowConfig,
  ActionFlowConfigStatus
} from '@atomic.io/common/types/action-flows'
import {getClient} from '../lib/api'
import {getTriggerEndpointWb} from '../lib/trigger-endpoint'
import {
  SfmcActionFlowFragment,
  useSfmcActionFlowsQuery
} from './generated/action-flows'

export type ActionFlowOption = {
  id: string
  name: string
  configStatus: ActionFlowConfigStatus

  // if not present, will show but be disabled
  sfConnectorUrl?: string

  currentConfig?: ActionFlowConfig
}

export const useSfmcActionFlows = ({
  organisationId,
  environmentId
}: {
  organisationId: string
  environmentId: string
}) => {
  const {data, loading} = useSfmcActionFlowsQuery({
    variables: {
      environmentId
    },
    client: getClient(organisationId)
  })

  const rawActionFlows = data?.actionFlowConfigs?.nodes

  const actionFlows = useMemo(
    () =>
      getValidatedActionFlows(
        {organisationId, environmentId},
        rawActionFlows ?? []
      ),
    [organisationId, environmentId, rawActionFlows]
  )

  return {actionFlows, loading}
}

const getValidatedActionFlows = (
  {
    organisationId,
    environmentId
  }: {organisationId: string; environmentId: string},
  actionFlows: SfmcActionFlowFragment[]
): ActionFlowOption[] => {
  const mut_options: ActionFlowOption[] = []

  for (const af of actionFlows) {
    const connectors = get(af.currentVersion?.config, 'triggers') ?? {}
    const connectorRuntimes = af.connectorRuntimes.nodes
    const mut_opt: ActionFlowOption = {
      id: af.id,
      name: af.name ?? '',
      configStatus: af.configStatus ?? 'DRAFT',
      currentConfig: af.currentVersion?.config as ActionFlowConfig
    }
    for (const connector of Object.values(connectors ?? {})) {
      const secret = connectorRuntimes.find(
        cr => cr.id === get(connector, 'id')
      )?.secret

      if (
        get(connector, 'type') === 'connector' &&
        get(connector, 'properties.connectorType') === 'sfmc-jb' &&
        secret
      ) {
        mut_opt.sfConnectorUrl = getTriggerEndpointWb({
          organisationId,
          environmentId,
          flowConfigId: af.id,
          secret: secret
        })
        break
      }
    }
    mut_options.push(mut_opt)
  }

  return mut_options
}

// TODO we don't want all the details here. Might need a custom resolver to
// just give us what SF's UI needs - required vars etc - and not sensitive
// data like auth secrets

gql`
  query SfmcActionFlows(
    $environmentId: String!
    $orderBy: [ActionFlowConfigsOrderBy!]! = [CONFIG_LAST_UPDATED_DESC]
  ) {
    actionFlowConfigs(
      condition: {environmentId: $environmentId}
      filter: {configStatus: {distinctFrom: ARCHIVED}}
      orderBy: $orderBy
    ) {
      nodes {
        ...SfmcActionFlow
      }
    }
  }

  fragment SfmcActionFlow on ActionFlowConfig {
    id
    name
    description
    configStatus
    # isDefault
    currentVersion {
      nodeId
      config
      status
    }

    connectorRuntimes: connectorRuntimesByEnvironmentIdAndFlowConfigId {
      nodes {
        id
        secret
      }
    }
  }
`
